import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Ruler, ChevronDown, ChevronUp, CheckCircle2, AlertCircle,
  Smartphone, Info, Save, RotateCcw, Layers, AlertTriangle, ExternalLink
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Brand token — matches main UI exactly
// ─────────────────────────────────────────────────────────────────────────────
const C = '#2D2926';

// ─────────────────────────────────────────────────────────────────────────────
// Britex search URL
// ─────────────────────────────────────────────────────────────────────────────
const britexSearch = name =>
  `https://www.britexfabrics.com/search?q=${encodeURIComponent(name)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Fabric data — retail price range from our library
// ─────────────────────────────────────────────────────────────────────────────
const FABRICS = {
  'brocade':        { name:'Brocade',            low:22, high:65  },
  'chiffon':        { name:'Chiffon',            low:12, high:45  },
  'crepe':          { name:'Crepe',              low:10, high:38  },
  'georgette':      { name:'Georgette',          low:14, high:42  },
  'jacquard':       { name:'Jacquard',           low:18, high:75  },
  'lace':           { name:'Lace',               low:15, high:95  },
  'linen':          { name:'Linen',              low:10, high:30  },
  'organza':        { name:'Organza',            low:12, high:50  },
  'satin':          { name:'Satin',              low:12, high:65  },
  'silk':           { name:'Silk',               low:18, high:85  },
  'tulle':          { name:'Tulle',              low:3,  high:35  },
  'velvet':         { name:'Velvet',             low:16, high:75  },
  'wool':           { name:'Wool',               low:15, high:80  },
  'deadstock-silk': { name:'Deadstock Silk',     low:15, high:60  },
  'organic-cotton': { name:'Organic Cotton',     low:10, high:28  },
  'tencel':         { name:'Tencel / Lyocell',   low:12, high:28  },
  'recycled-poly':  { name:'Recycled Polyester', low:8,  high:22  },
  'custom':         { name:'Custom',             low:10, high:60  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Context-adjusted price band
// ─────────────────────────────────────────────────────────────────────────────
const GOAL_BAND = {
  'business': { range:[0.00, 0.45], note:'business weight' },
  'cocktail': { range:[0.20, 0.65], note:'cocktail weight' },
  'gala':     { range:[0.40, 0.85], note:'gala grade'      },
  'bridal':   { range:[0.45, 0.95], note:'bridal grade'    },
};
const SILHOUETTE_SHIFT = {
  'Ball Gown':0.10,'Mermaid':0.08,'Trumpet':0.06,
  'Slip':-0.08,'Empire':-0.05,'Drop-Waist':-0.05,
};

function contextPricePerYard(fabricId, designGoal, silhouette) {
  const f     = FABRICS[fabricId] || FABRICS['custom'];
  const goal  = GOAL_BAND[designGoal] || GOAL_BAND['cocktail'];
  const shift = SILHOUETTE_SHIFT[silhouette] || 0;
  const spread = f.high - f.low;
  const lo = Math.max(0, Math.min(1, goal.range[0] + shift));
  const hi = Math.max(0, Math.min(1, goal.range[1] + shift));
  return {
    low:  Math.round(f.low + spread * lo),
    high: Math.round(f.low + spread * hi),
    note: goal.note,
  };
}

function estimateCost(yardage, fabricId, designGoal, silhouette) {
  const ppy = contextPricePerYard(fabricId, designGoal, silhouette);
  const isRange = typeof yardage === 'object';
  const costLow  = (isRange ? parseFloat(yardage.min) : parseFloat(yardage)) * ppy.low;
  const costHigh = (isRange ? parseFloat(yardage.max) : parseFloat(yardage)) * ppy.high;
  return {
    low:         Math.round(costLow  / 5) * 5,
    high:        Math.round(costHigh / 5) * 5,
    perYardLow:  ppy.low,
    perYardHigh: ppy.high,
    note:        ppy.note,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Design goal labels
// ─────────────────────────────────────────────────────────────────────────────
const GOAL_LABELS = {
  'business':'Business','cocktail':'Cocktail',
  'gala':'Gala / Black Tie','bridal':'Bridal',
};

// ─────────────────────────────────────────────────────────────────────────────
// Measurement groups
// ─────────────────────────────────────────────────────────────────────────────
const GROUPS = [
  {
    id:'essential', label:'Essential', description:'Required for every design',
    fields:[
      { key:'height',      label:'Height',             hint:'Crown of head to floor. Stand tall, feet together.',            min:54, max:84,  step:0.5  },
      { key:'bust',        label:'Bust',               hint:'Fullest part of the chest, tape parallel to floor.',            min:28, max:60,  step:0.5  },
      { key:'highBust',    label:'High Bust',          hint:'Just under the arms, above the bust. Critical for bodice fit.', min:26, max:58,  step:0.5  },
      { key:'waist',       label:'Natural Waist',      hint:'Narrowest point of the torso, usually above the navel.',        min:22, max:52,  step:0.5  },
      { key:'hips',        label:'Hips',               hint:'Fullest part of the hips, 7–9" below natural waist.',           min:28, max:62,  step:0.5  },
      { key:'hollowToHem', label:'Hollow to Hem',      hint:'Neckline hollow to desired hem. Stand in your occasion heels.', min:28, max:80,  step:0.5  },
    ],
  },
  {
    id:'upper', label:'Upper Body', description:'For sleeve and shoulder precision',
    fields:[
      { key:'shoulder',     label:'Shoulder Width',    hint:'Seam to seam across the back, shoulder bone to shoulder bone.', min:12, max:22,  step:0.25 },
      { key:'sleeveLength', label:'Sleeve Length',     hint:'Shoulder seam to wrist bone, arm slightly bent.',               min:18, max:36,  step:0.5  },
      { key:'bicep',        label:'Bicep',             hint:'Fullest part of the upper arm, flexed.',                        min:8,  max:22,  step:0.5  },
    ],
  },
  {
    id:'torso', label:'Torso', description:'For bodice length and proportion',
    fields:[
      { key:'frontWaist',  label:'Front Waist Length', hint:'Hollow of neck, over bust, to natural waist — front.',          min:12, max:22,  step:0.25 },
      { key:'backWaist',   label:'Back Waist Length',  hint:'Nape of neck to natural waist — back. Often differs from front.',min:12, max:22,  step:0.25 },
      { key:'waistToHip',  label:'Waist to Hip',       hint:'Natural waist down to fullest hip point.',                      min:6,  max:16,  step:0.25 },
    ],
  },
  {
    id:'notes', label:'Fit Notes', description:'Optional but invaluable for your maker',
    fields:[
      { key:'posture',       label:'Posture Note',     hint:'e.g., Slightly rounded upper back, one shoulder higher…', type:'text' },
      { key:'fitPreference', label:'Fit Preference',   hint:'e.g., I prefer room through the hips, fitted bodice…',   type:'text' },
    ],
  },
];

const ESSENTIAL_KEYS = GROUPS[0].fields.map(f => f.key);
const UPPER_KEYS     = GROUPS[1].fields.map(f => f.key);
const TORSO_KEYS     = GROUPS[2].fields.map(f => f.key);
const ALL_NUM_KEYS   = GROUPS.flatMap(g => g.fields).filter(f => !f.type).map(f => f.key);

// ─── Preview preset measurements ─────────────────────────────────────────────
// Realistic sample measurements for three body types — for exploration only
const PREVIEW_PRESETS = {
  petite: {
    label: "Petite",
    desc:  "5′2″ · Size 6–8",
    height:"62", bust:"34", highBust:"32", waist:"26", hips:"36",
    hollowToHem:"54", shoulder:"13", sleeveLength:"21", bicep:"10",
    frontWaist:"14.5", backWaist:"14", waistToHip:"7",
    posture:"", fitPreference:"",
  },
  average: {
    label: "Average",
    desc:  "5′5″ · Size 10–12",
    height:"65", bust:"38", highBust:"36", waist:"30", hips:"40",
    hollowToHem:"57", shoulder:"14.5", sleeveLength:"23", bicep:"11.5",
    frontWaist:"16", backWaist:"15.5", waistToHip:"8",
    posture:"", fitPreference:"",
  },
  tall: {
    label: "Tall",
    desc:  "5′9″ · Size 12–14",
    height:"69", bust:"40", highBust:"38", waist:"32", hips:"42",
    hollowToHem:"62", shoulder:"15", sleeveLength:"25", bicep:"12",
    frontWaist:"17", backWaist:"16.5", waistToHip:"8.5",
    posture:"", fitPreference:"",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Cross-field validation
// ─────────────────────────────────────────────────────────────────────────────
const CROSS_RULES = [
  { keys:['waist','bust'],           test:m=>parseFloat(m.waist)>=parseFloat(m.bust),                      message:'Waist is larger than bust — double-check both measurements.',                                  severity:'warn'  },
  { keys:['waist','hips'],           test:m=>parseFloat(m.waist)>=parseFloat(m.hips),                      message:'Waist is larger than hips — possible but uncommon. Confirm.',                                 severity:'warn'  },
  { keys:['highBust','bust'],        test:m=>parseFloat(m.highBust)>parseFloat(m.bust),                    message:'High bust should be ≤ full bust. Re-measure.',                                                severity:'error' },
  { keys:['bust','hips'],            test:m=>Math.abs(parseFloat(m.bust)-parseFloat(m.hips))>18,           message:'Bust and hip differ by more than 18" — worth double-checking.',                               severity:'warn'  },
  { keys:['hollowToHem','height'],   test:m=>parseFloat(m.hollowToHem)>parseFloat(m.height),               message:'Hollow-to-hem exceeds total height — this is only possible with a train. Re-check both measurements.', severity:'error' },
  { keys:['hollowToHem','height'],   test:m=>parseFloat(m.hollowToHem)>=parseFloat(m.height)*0.93&&parseFloat(m.hollowToHem)<=parseFloat(m.height), message:'Hollow-to-hem is very close to total height — measured in heels, or is this a train length?', severity:'warn'  },
  { keys:['frontWaist','backWaist'], test:m=>Math.abs(parseFloat(m.frontWaist)-parseFloat(m.backWaist))>3, message:'Front and back waist lengths differ by more than 3". Confirm technique was the same.',         severity:'warn'  },
  { keys:['shoulder','bust'],        test:m=>parseFloat(m.shoulder)>parseFloat(m.bust)/2,                  message:'Shoulder width seems wider than half the bust — re-check.',                                   severity:'warn'  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Yardage
// ─────────────────────────────────────────────────────────────────────────────
const YARDAGE_BASE = {
  'A-line':        { wide:[3.0,6.0], mid:[3.5,5.5], precise:4.5  },
  'Ball Gown':     { wide:[7.0,16.0],mid:[8.0,14.0],precise:11.0 },
  'Drop-Waist':    { wide:[3.5,6.5], mid:[4.0,6.0], precise:5.0  },
  'Empire':        { wide:[3.0,6.0], mid:[3.5,5.5], precise:4.25 },
  'Fit-and-Flare': { wide:[3.0,6.0], mid:[3.5,5.5], precise:4.5  },
  'Mermaid':       { wide:[4.0,8.0], mid:[4.5,7.0], precise:5.75 },
  'Sheath':        { wide:[2.5,5.0], mid:[3.0,4.5], precise:3.5  },
  'Slip':          { wide:[2.5,5.0], mid:[3.0,4.5], precise:3.5  },
  'Trumpet':       { wide:[3.5,7.0], mid:[4.0,6.5], precise:5.0  },
  'Custom':        { wide:[3.5,8.0], mid:[4.0,7.0], precise:5.5  },
};
const FABRIC_MULT = {
  'brocade':1.2,'chiffon':1.3,'crepe':1.0,'georgette':1.2,'jacquard':1.25,
  'lace':1.3,'linen':1.1,'organza':1.2,'satin':1.1,'silk':1.1,
  'tulle':2.0,'velvet':1.15,'wool':1.1,'deadstock-silk':1.1,
  'organic-cotton':1.0,'tencel':1.0,'recycled-poly':1.0,'custom':1.15,
};
const LINING_MULT = {
  'Fully Lined':                    0.85,  // near-full yardage again in lining fabric
  'Partially Lined (bodice only)':  0.40,  // bodice portion only
  'Self-Lined':                     0.00,  // no additional fabric needed
  'Unlined':                        0.00,
  "To Maker's Discretion":          0.00,  // maker decides — no calculation
};

const torsoAdj = m => {
  const fw=parseFloat(m.frontWaist)||0, bw=parseFloat(m.backWaist)||0;
  if (!fw||!bw) return 0;
  return ((fw+bw)/2-16)*0.1;
};

// ─────────────────────────────────────────────────────────────────────────────
// Ease
// ─────────────────────────────────────────────────────────────────────────────
const EASE_MAP = {
  'Sheath':0.5,'Mermaid':0.75,'Trumpet':0.75,'Slip':0.5,
  'A-line':1.0,'Empire':1.0,'Fit-and-Flare':1.0,'Drop-Waist':1.0,
  'Ball Gown':1.5,'Custom':1.0,
};
const getEase = s => {
  const e=EASE_MAP[s]||1.0;
  if (e<=0.5)  return { label:'Close Fit',     note:'Precision cutting. Very little ease.' };
  if (e<=0.75) return { label:'Semi-Fitted',   note:'Light ease for comfort without volume.' };
  if (e<=1.0)  return { label:'Standard Ease', note:'Classic fit allowance for this silhouette.' };
  return             { label:'Full Ease',      note:'Generous ease — movement and volume.' };
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles — aligned to main UI token system
// fontFamily: system-ui throughout (main UI uses no serif)
// borderRadius: 24 on cards (main UI standard)
// border: 1px solid #f0eeec (main UI card border)
// fontSize: 12 for body, 10 for labels (main UI scale)
// placeholder / muted text: #b8b0a8 (main UI ph color)
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page: { margin:0, padding:0, minHeight:'100vh', backgroundColor:'#FDFCF9', color:C,
          fontFamily:'system-ui,sans-serif', fontSize:14, lineHeight:1.4 },
  hdr:  { margin:0, padding:'14px 24px', backgroundColor:'#fff',
          borderBottom:'1px solid #e7e5e4', display:'flex', alignItems:'center',
          position:'sticky', top:0, zIndex:50 },
  main: { margin:'0 auto', padding:'0 20px 64px', maxWidth:680,
          display:'flex', flexDirection:'column', gap:20 },
  card: { margin:0, padding:24, backgroundColor:'#fff', borderRadius:24,
          border:'1px solid #f0eeec', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' },
  lbl:  { display:'block', margin:0, marginBottom:6, fontSize:10, fontWeight:700,
          letterSpacing:'0.15em', color:'#a8a29e', textTransform:'uppercase',
          fontFamily:'system-ui,sans-serif' },
  inp:  { display:'block', margin:0, width:'100%', padding:'10px 14px',
          backgroundColor:'#fafaf9', borderRadius:10, border:'1px solid #e7e5e4',
          fontSize:12, outline:'none', fontFamily:'system-ui,sans-serif', color:'#1c1917' },
  hint: { margin:0, marginTop:5, fontSize:10, color:'#b8b0a8', fontStyle:'italic',
          lineHeight:1.5, fontFamily:'system-ui,sans-serif' },
  tag:  { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px',
          borderRadius:999, fontSize:9, fontWeight:700, letterSpacing:'0.12em',
          textTransform:'uppercase', fontFamily:'system-ui,sans-serif' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Step header
// ─────────────────────────────────────────────────────────────────────────────
function StepHeader({ silhouette, fabricId, designGoal }) {
  const fabric = FABRICS[fabricId] || FABRICS['custom'];
  const goal   = GOAL_LABELS[designGoal] || designGoal;
  return (
    <div style={{ backgroundColor:'#fff', borderBottom:'1px solid #e7e5e4',
      padding:'22px 24px 20px', marginLeft:-20, marginRight:-20, marginBottom:4 }}>

      {/* Top row: step 1 receded · active title centred · step 3 receded */}
      <div style={{display:'flex', alignItems:'center',
        justifyContent:'space-between', marginBottom:16}}>

        {/* Step 1 — done, quiet */}
        <div style={{display:'flex', alignItems:'center', gap:6, minWidth:70}}>
          <div style={{width:18, height:18, borderRadius:'50%', flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            backgroundColor:'#f0eeec', border:'1.5px solid #e0dbd4'}}>
            <CheckCircle2 style={{width:10, height:10, color:'#c4bfba'}}/>
          </div>
          <span style={{fontSize:10, fontWeight:400, color:'#c4bfba',
            fontFamily:'system-ui,sans-serif'}}>The Studio</span>
        </div>

        {/* Step 2 — active, dominant */}
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <div style={{
            width:34, height:34, borderRadius:'50%', flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            backgroundColor:'#C9A0A0', boxShadow:'0 2px 8px rgba(45,41,38,0.22)',
          }}>
            <span style={{fontSize:14, fontWeight:700, color:'#fff',
              fontFamily:'system-ui,sans-serif', lineHeight:1}}>2</span>
          </div>
          <span style={{fontSize:17, fontWeight:700, color:C,
            fontFamily:'system-ui,sans-serif', letterSpacing:'-0.02em'}}>
            Measurements
          </span>
        </div>

        {/* Step 3 — upcoming, quiet */}
        <div style={{display:'flex', alignItems:'center', gap:6,
          minWidth:70, justifyContent:'flex-end'}}>
          <span style={{fontSize:10, fontWeight:400, color:'#c4bfba',
            fontFamily:'system-ui,sans-serif'}}>The Drawing Room</span>
          <div style={{width:18, height:18, borderRadius:'50%', flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            backgroundColor:'#f5f3f0', border:'1.5px solid #e8e4e0'}}>
            <span style={{fontSize:9, fontWeight:600, color:'#d4d0cc',
              fontFamily:'system-ui,sans-serif'}}>3</span>
          </div>
        </div>
      </div>

      {/* Design brief pills */}
      <div style={{display:'flex', alignItems:'center', gap:7, flexWrap:'wrap',
        padding:'9px 14px', backgroundColor:'#f5f4f2', borderRadius:12}}>
        <span style={{fontSize:9, color:'#a8a29e', fontFamily:'system-ui,sans-serif',
          textTransform:'uppercase', letterSpacing:'0.15em', fontWeight:700}}>For</span>
        <span style={{width:1, height:10, backgroundColor:'#ddd9d5', flexShrink:0}}/>
        {[goal, silhouette, fabric.name].map((item, i) => (
          <span key={i} style={{fontSize:11, color:C, fontFamily:'system-ui,sans-serif',
            fontWeight:600, padding:'2px 10px', backgroundColor:'#fff',
            borderRadius:999, border:'1px solid #e7e5e4'}}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Number input with +/− buttons
// ─────────────────────────────────────────────────────────────────────────────
function NumInput({ field, value, onChange, flagged }) {
  if (field.type==='text') {
    return (
      <textarea
        style={{...S.inp, minHeight:56, resize:'vertical', lineHeight:1.5,
          fontStyle:value?'normal':'italic', color:value?'#1c1917':'#b8b0a8'}}
        placeholder={field.hint} value={value}
        onChange={e=>onChange(field.key,e.target.value)}
      />
    );
  }
  const nudge = dir => {
    const next=Math.max(field.min,Math.min(field.max,
      +(((parseFloat(value)||0)+dir*field.step)).toFixed(field.step === 0.25 ? 2 : 1)));
    onChange(field.key,String(next));
  };
  const bc=flagged?'#d4a896':'#e7e5e4';
  const bg=flagged?'#fdf8f7':'#fafaf9';
  const btn = side => ({
    margin:0,padding:0,width:38,height:40,cursor:'pointer',fontSize:18,
    color:'#78716c',backgroundColor:'#f5f3f0',fontFamily:'system-ui,sans-serif',lineHeight:1,
    border:`1px solid ${bc}`,
    borderRadius:side==='L'?'10px 0 0 10px':'0 10px 10px 0',
    borderRight:side==='L'?'none':`1px solid ${bc}`,
    borderLeft:side==='R'?'none':`1px solid ${bc}`,
  });
  return (
    <div style={{display:'flex',alignItems:'stretch'}}>
      <button onClick={()=>nudge(-1)} style={btn('L')}>−</button>
      <input type="number" min={field.min} max={field.max} step={field.step}
        style={{...S.inp,borderRadius:0,textAlign:'center',fontWeight:600,
          fontSize:13,height:40,border:`1px solid ${bc}`,
          borderLeft:'none',borderRight:'none',backgroundColor:bg}}
        placeholder="—" value={value}
        onChange={e=>onChange(field.key,e.target.value)}
      />
      <button onClick={()=>nudge(1)} style={btn('R')}>+</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Yardage + Cost hero card
// ─────────────────────────────────────────────────────────────────────────────
function YardageCard({ tier, yardage, fabricId, silhouette, designGoal, ease, lining }) {
  const fabric   = FABRICS[fabricId]||FABRICS['custom'];
  const isActive = tier!==null && yardage!==null;
  const cost     = isActive ? estimateCost(yardage,fabricId,designGoal,silhouette) : null;

  const yardsDisplay = !isActive ? null
    : tier==='precise' ? `${yardage}` : `${yardage.min}–${yardage.max}`;

  const costDisplay = cost
    ? (cost.low===cost.high ? `$${cost.low}` : `$${cost.low}–$${cost.high}`)
    : null;

  const nextHint =
    tier==='wide' ? 'Fill Torso to narrow the range' :
    tier==='mid'  ? 'Fill Upper Body for a precise figure' : null;

  return (
    <div style={{...S.card, padding:0, overflow:'hidden',
      border:isActive?'1px solid #2a2420':'1px solid #1a1714',
      transition:'border-color 0.4s ease'}}>

      {/* Dark top — activates once essentials filled */}
      <div style={{
        padding:isActive?'28px 32px 26px':'22px 28px',
        backgroundColor:isActive?'#1a1714':'#2a2420',
        transition:'background-color 0.5s ease',
      }}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.25em',textTransform:'uppercase',
          fontFamily:'system-ui,sans-serif',
          color:isActive?'#C9A0A0':'rgba(201,160,160,0.5)',marginBottom:10}}>
          Estimated Fabric
        </div>

        {isActive ? (
          <div>
            {/* Top row — yardage left, cost right */}
            <div style={{display:'flex',alignItems:'flex-start',
              justifyContent:'space-between',flexWrap:'wrap',gap:16}}>

              {/* Yardage */}
              <div>
                <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
                  <div style={{fontSize:tier==='precise'?50:44,fontWeight:600,color:'#fff',
                    lineHeight:1,fontFamily:'system-ui,sans-serif',letterSpacing:'-0.03em'}}>
                    {yardsDisplay}
                  </div>
                  <div style={{fontSize:17,color:'rgba(255,255,255,0.65)',
                    fontFamily:'system-ui,sans-serif',marginBottom:2}}>yards</div>
                  {tier==='precise' && (
                    <div style={{display:'inline-flex',alignItems:'center',gap:4,
                      padding:'3px 9px',borderRadius:999,backgroundColor:'rgba(255,255,255,0.1)',
                      fontSize:9,fontWeight:700,letterSpacing:'0.14em',
                      color:'rgba(255,255,255,0.6)',textTransform:'uppercase',
                      fontFamily:'system-ui,sans-serif',marginBottom:2}}>
                      <CheckCircle2 style={{width:8,height:8}}/> Precise
                    </div>
                  )}
                </div>
                {/* Fabric link + silhouette */}
                <div style={{marginTop:8,display:'flex',alignItems:'center',gap:6}}>
                  <a href={britexSearch(fabric.name)} target="_blank" rel="noopener noreferrer"
                    style={{display:'inline-flex',alignItems:'center',gap:4,
                      fontSize:12,color:'#fff',
                      fontFamily:'system-ui,sans-serif',fontWeight:700,
                      textDecoration:'underline',textUnderlineOffset:3,
                      textDecorationColor:'rgba(255,255,255,0.9)'}}>
                    {fabric.name}
                    <ExternalLink style={{width:10,height:10,opacity:1}}/>
                  </a>
                  <span style={{color:'rgba(255,255,255,0.5)',fontSize:12}}>·</span>
                  <span style={{fontSize:12,color:'rgba(255,255,255,0.92)',
                    fontFamily:'system-ui,sans-serif',fontWeight:400}}>
                    {silhouette}
                  </span>
                </div>
              </div>

              {/* Cost right */}
              {costDisplay && (
                <div style={{padding:'14px 20px',borderRadius:16,flexShrink:0,
                  backgroundColor:'rgba(255,255,255,0.06)',
                  border:'1px solid rgba(255,255,255,0.1)',textAlign:'right'}}>
                  <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.2em',
                    textTransform:'uppercase',fontFamily:'system-ui,sans-serif',
                    color:'rgba(255,255,255,0.60)',marginBottom:6}}>
                    Fabric Cost Est.
                  </div>
                  <div style={{fontSize:26,fontWeight:600,color:'#fff',lineHeight:1,
                    fontFamily:'system-ui,sans-serif',letterSpacing:'-0.02em'}}>
                    {costDisplay}
                  </div>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',
                    gap:5,marginTop:7}}>
                    <div style={{width:3,height:3,borderRadius:'50%',
                      backgroundColor:'rgba(255,255,255,0.7)'}}/>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.82)',
                      fontFamily:'system-ui,sans-serif',fontWeight:400,letterSpacing:'0.02em'}}>
                      {cost.note} · ${cost.perYardLow}–${cost.perYardHigh}/yd
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Britex sourcing note — full width black box below */}
            <div style={{marginTop:14,padding:'14px 18px',borderRadius:14,
              backgroundColor:'#C9A0A0',
              border:'1px solid rgba(201,160,160,0.3)'}}>
              <div style={{fontSize:8,fontWeight:700,letterSpacing:'0.2em',
                textTransform:'uppercase',fontFamily:'system-ui,sans-serif',
                color:'rgba(255,255,255,0.65)',marginBottom:5}}>
                Sourcing
              </div>
              <p style={{margin:0,fontSize:11,lineHeight:1.65,
                fontStyle:'italic',fontWeight:300,
                color:'#fff',
                fontFamily:'system-ui,sans-serif'}}>
                Britex Fabrics on Post Street in San Francisco has been a destination for designers and makers since 1952 — runway silks alongside everyday linens, at a range of price points. Our recommended starting point for sourcing your fabric.
              </p>
            </div>
          </div>
        ) : (
          <div style={{fontSize:13,color:'rgba(255,255,255,0.75)',fontStyle:'italic',
            fontFamily:'system-ui,sans-serif'}}>
            Fill essential measurements to calculate
          </div>
        )}
      </div>

      {/* Bottom strip */}
      {isActive && (
        <div style={{padding:'13px 32px',display:'flex',alignItems:'center',
          justifyContent:'space-between',flexWrap:'wrap',gap:12,
          borderTop:'1px solid #f0eeec',backgroundColor:'#fff',
          animation:'fadeIn 0.3s ease'}}>
          <div>
            <span style={{fontSize:11,fontWeight:700,color:C,
              fontFamily:'system-ui,sans-serif'}}>{ease.label}</span>
            <span style={{fontSize:10,color:'#57534e',
              fontFamily:'system-ui,sans-serif'}}> — {ease.note}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <div style={{width:3,height:3,borderRadius:'50%',backgroundColor:'#C9A0A0',flexShrink:0}} />
            <span style={{fontSize:10,fontFamily:'system-ui,sans-serif',color:'#78716c',fontStyle:'italic'}}>
              {!lining || lining === 'Fully Lined'
                ? 'Includes lining allowance · Fully Lined · +85%'
                : lining === 'Partially Lined (bodice only)'
                ? 'Includes lining allowance · Partially Lined (bodice only) · +40%'
                : lining === 'Self-Lined'
                ? 'Self-lined · no additional fabric'
                : lining === 'Unlined'
                ? 'Unlined · no lining allowance'
                : 'Lining at maker’s discretion · not calculated'}
            </span>
          </div>
          {nextHint && (
            <div style={{fontSize:10,color:'#57534e',fontStyle:'italic',
              fontFamily:'system-ui,sans-serif',textAlign:'right'}}>
              {nextHint}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation alerts
// ─────────────────────────────────────────────────────────────────────────────
function ValidationAlerts({ alerts }) {
  if (!alerts.length) return null;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:8,animation:'fadeIn 0.25s ease'}}>
      {alerts.map((a,i)=>(
        <div key={i} style={{display:'flex',gap:10,padding:'10px 16px',borderRadius:12,
          backgroundColor:a.severity==='error'?'#fdf3f3':'#fdfaf3',
          border:`1px solid ${a.severity==='error'?'#e8c8c8':'#e8dfc4'}`}}>
          <AlertTriangle style={{width:13,height:13,flexShrink:0,marginTop:2,
            color:a.severity==='error'?'#c07070':'#b8963c'}}/>
          <span style={{fontSize:11,color:'#5c5248',lineHeight:1.6,
            fontFamily:'system-ui,sans-serif',fontStyle:'italic'}}>
            {a.message}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const INITIAL = {
  height:'',bust:'',highBust:'',waist:'',hips:'',hollowToHem:'',
  shoulder:'',sleeveLength:'',bicep:'',
  frontWaist:'',backWaist:'',waistToHip:'',
  posture:'',fitPreference:'',skinTone:'',
};

const SKIN_TONE_OPTIONS = [
  { value:'fair-cool',   label:'Fair & Cool',   swatch:'#F5E6DA', imagenDesc:'fair cool complexion, porcelain skin tone' },
  { value:'fair-warm',   label:'Fair & Warm',   swatch:'#F2D5B8', imagenDesc:'fair warm complexion, light golden skin tone' },
  { value:'medium-warm', label:'Medium & Warm', swatch:'#D4956A', imagenDesc:'medium warm complexion, golden tan skin tone' },
  { value:'medium-cool', label:'Medium & Cool', swatch:'#C17E5A', imagenDesc:'medium cool complexion, olive skin tone' },
  { value:'deep-warm',   label:'Deep & Warm',   swatch:'#8B5E3C', imagenDesc:'deep warm complexion, rich brown skin tone' },
  { value:'deep-cool',   label:'Deep & Cool',   swatch:'#5C3A28', imagenDesc:'deep cool complexion, deep brown skin tone' },
];

export default function Measurements({
  form               = {},
  designRender       = null,
  initialMeasurements = null,
  silhouette = 'A-line',
  fabricId   = 'silk',
  designGoal = 'cocktail',
  lining     = 'Fully Lined',
  onMeasurementsDataChange = function() {},
  onProceedToSalon   = function() {},
  onReturnToStudio   = function() {},
}) {
  const [m, setM]         = useState(function() {
    if (initialMeasurements && Object.keys(initialMeasurements).some(function(k) { return !!initialMeasurements[k]; })) {
      return Object.assign({}, INITIAL, initialMeasurements);
    }
    return INITIAL;
  });
  const [open, setOpen]   = useState({ essential:true, upper:false, torso:false, notes:false });
  const [saved,       setSaved]       = useState(false);
  const [consent,     setConsent]     = useState(false);
  const [usingPreset, setUsingPreset] = useState(null); // 'petite' | 'average' | 'tall' | null

  const applyPreset = useCallback(function(presetKey) {
    const preset = PREVIEW_PRESETS[presetKey];
    if (!preset) return;
    // Apply all preset fields
    setM(function() {
      const base = {};
      ALL_NUM_KEYS.forEach(function(k) { base[k] = preset[k] || ''; });
      base.posture = preset.posture || '';
      base.fitPreference = preset.fitPreference || '';
      return base;
    });
    setUsingPreset(presetKey);
    setConsent(false); // presets never count as consent
    setSaved(false);
  }, []);

  const clearPreset = useCallback(function() {
    setM(function() {
      const base = {};
      ALL_NUM_KEYS.forEach(function(k) { base[k] = ''; });
      base.posture = '';
      base.fitPreference = '';
      return base;
    });
    setUsingPreset(null);
    setConsent(false);
    setSaved(false);
  }, []);

  const change = useCallback((key,val)=>{ setSaved(false); setM(p=>({...p,[key]:val})); },[]);
  const toggle = id => setOpen(p=>({...p,[id]:!p[id]}));

  const esssFilled  = ESSENTIAL_KEYS.filter(k=>m[k]!=='').length;
  const torsoFilled = TORSO_KEYS.filter(k=>m[k]!=='').length;
  const allFilled   = ALL_NUM_KEYS.filter(k=>m[k]!=='').length;
  const essComplete   = esssFilled  === ESSENTIAL_KEYS.length;
  const torsoComplete = torsoFilled === TORSO_KEYS.length;
  const allComplete   = allFilled   === ALL_NUM_KEYS.length;

  const alerts = useMemo(()=>
    CROSS_RULES.filter(r=>r.keys.every(k=>m[k]!=='')&&r.test(m))
  ,[m]);
  const flaggedKeys = useMemo(()=>{
    const s=new Set(); alerts.forEach(a=>a.keys.forEach(k=>s.add(k))); return s;
  },[alerts]);
  const hasErrors = alerts.some(a=>a.severity==='error');

  const yardageResult = useMemo(()=>{
    if (!essComplete) return { tier:null, yardage:null };
    const base=YARDAGE_BASE[silhouette]||YARDAGE_BASE['Custom'];
    const mult=FABRIC_MULT[fabricId]||1.1;
    const lm=LINING_MULT[lining]??0;
    const adj=torsoAdj(m);
    if (allComplete)   return { tier:'precise', yardage:((base.precise+adj)*mult*(1+lm)).toFixed(1) };
    if (torsoComplete) return { tier:'mid', yardage:{ min:((base.mid[0]+adj)*mult*(1+lm)).toFixed(1), max:((base.mid[1]+adj)*mult*(1+lm)).toFixed(1) }};
    return { tier:'wide', yardage:{ min:(base.wide[0]*mult*(1+lm)).toFixed(1), max:(base.wide[1]*mult*(1+lm)).toFixed(1) }};
  },[essComplete,torsoComplete,allComplete,silhouette,fabricId,lining,m]);

  const ease = getEase(silhouette);

  const proportionNote = useMemo(()=>{
    const b=parseFloat(m.bust)||0,w=parseFloat(m.waist)||0,h=parseFloat(m.hips)||0;
    if (!b||!w||!h) return null;
    const drop=h-w;
    if (drop>=10) return "Defined waist — consider princess seaming for optimal fit through the torso.";
    if (drop>=7)  return "Moderate waist definition — standard adjustments should fit well.";
    return "Straighter torso — a dart adjustment through the waist may refine the fit.";
  },[m]);

  // Presets: can proceed to Salon for exploration, but consent is never granted
  const isPreview = !!usingPreset;
  const canSave   = essComplete && !hasErrors && (consent || isPreview);

  const costResult = useMemo(function() {
    if (!yardageResult.tier || !yardageResult.yardage) return null;
    return estimateCost(yardageResult.yardage, fabricId, designGoal, silhouette);
  }, [yardageResult, fabricId, designGoal, silhouette]);

  useEffect(function() {
    onMeasurementsDataChange({
      measurements: m,
      fabricYardage: yardageResult.tier ? yardageResult : null,
      costEstimate: costResult,
      consentGiven: consent,
      skinTone: m.skinTone || '',
    });
  }, [m, yardageResult, costResult, consent, onMeasurementsDataChange]);

  const handleProceed = useCallback(function() {
    onProceedToSalon({
      measurements: m,
      fabricYardage: yardageResult.tier ? yardageResult : null,
      costEstimate: costResult,
      consentGiven: isPreview ? false : consent, // presets never grant consent
      isPreviewMeasurements: isPreview,
      skinTone: m.skinTone || '',
    });
  }, [m, yardageResult, costResult, consent, isPreview, onProceedToSalon]);

  return (
    <div style={S.page}>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none}
        input[type=number]{-moz-appearance:textfield}
        input::placeholder,textarea::placeholder{font-style:italic;color:#b8b0a8}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        button:hover{opacity:0.8;transition:opacity 0.15s ease}
        a:hover{opacity:0.7;transition:opacity 0.15s ease}
      `}</style>

      {/* Sticky nav */}
      <div style={S.hdr}>
        <Layers style={{color:C,marginRight:10,width:20,height:20,flexShrink:0}}/>
        <div style={{flex:1}}>
          <div style={{fontSize:17,fontWeight:600,letterSpacing:'-0.02em',
            fontFamily:'system-ui,sans-serif'}}>Tabitha</div>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.3em',color:'#a8a29e',
            textTransform:'uppercase',fontFamily:'system-ui,sans-serif'}}>The Fitting Room</div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:9,color:'#b8b0a8',fontFamily:'system-ui,sans-serif',
            textTransform:'uppercase',letterSpacing:'0.12em'}}>Measuring with</div>
          <div style={{fontSize:11,fontWeight:700,color:C,letterSpacing:'0.08em',
            fontFamily:'system-ui,sans-serif'}}>Choozr App</div>
        </div>
      </div>

      {/* Step header */}
      <div style={{maxWidth:680,margin:'0 auto',padding:'0 20px'}}>
        <StepHeader silhouette={silhouette} fabricId={fabricId} designGoal={designGoal}/>
      </div>

      <div style={S.main}>

        {/* ── ORDER: Estimated Fabric → Choozr → Alerts → Profile → Measurements ── */}

        {/* 1. Yardage + Cost hero — first thing seen */}
        <YardageCard
          tier={yardageResult.tier}
          yardage={yardageResult.yardage}
          fabricId={fabricId}
          silhouette={silhouette}
          designGoal={designGoal}
          ease={ease}
          lining={lining}
        />

        {/* 2. Choozr callout */}
        <div style={{padding:'14px 18px',backgroundColor:'#fafaf9',borderRadius:16,
          border:'1px solid #f0eeec',display:'flex',gap:12,alignItems:'flex-start'}}>
          <Smartphone style={{width:16,height:16,color:C,flexShrink:0,marginTop:2}}/>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:C,marginBottom:4,
              fontFamily:'system-ui,sans-serif',letterSpacing:'0.06em',textTransform:'uppercase'}}>
              Recommended: Choozr App
            </div>
            <div style={{fontSize:11,color:'#78716c',lineHeight:1.6,fontFamily:'system-ui,sans-serif'}}>
              Two photos, under two minutes, no tape measure required. Even careful
              self-measurement with a tape can drift by ½" or more — enough to affect
              fit at the bodice and hip. 100% GDPR compliant —
              your face is always blurred, and you control your data completely.{' '}
              <a href="https://choozr.ai" target="_blank" rel="noopener noreferrer"
                style={{color:C,fontWeight:700,textDecoration:'underline',textUnderlineOffset:3}}>
                choozr.ai →
              </a>
            </div>
          </div>
        </div>

        {/* 3. Validation alerts */}
        <ValidationAlerts alerts={alerts}/>

        {/* 4. Profile progress */}
        <div style={S.card}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <Ruler style={{width:14,height:14,color:'#a8a29e'}}/>
              <span style={{fontSize:10,fontWeight:700,letterSpacing:'0.15em',color:'#a8a29e',
                textTransform:'uppercase',fontFamily:'system-ui,sans-serif'}}>
                Measurement Profile
              </span>
            </div>
            <span style={{fontSize:10,fontWeight:700,fontFamily:'system-ui,sans-serif',
              color:allComplete?'#5a9a5a':'#a8a29e'}}>
              {allFilled} / {ALL_NUM_KEYS.length} fields
            </span>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'6fr 3fr 3fr 2fr',gap:4,marginBottom:14}}>
            {[
              { id:'essential',label:'Essential',total:ESSENTIAL_KEYS.length,filled:esssFilled },
              { id:'upper',    label:'Upper',    total:UPPER_KEYS.length,    filled:UPPER_KEYS.filter(k=>m[k]!=='').length },
              { id:'torso',    label:'Torso',    total:TORSO_KEYS.length,    filled:torsoFilled },
              { id:'notes',    label:'Notes',    total:2,                    filled:[m.posture,m.fitPreference].filter(Boolean).length },
            ].map(seg=>{
              const done=seg.filled===seg.total;
              return (
                <div key={seg.id}>
                  <div style={{height:4,backgroundColor:'#f0eeec',borderRadius:999,
                    overflow:'hidden',marginBottom:5}}>
                    <div style={{height:'100%',borderRadius:999,transition:'width 0.4s ease',
                      backgroundColor:done?'#6aa36a':seg.filled>0?'#C9A0A0':'#f0eeec',
                      width:`${Math.round(seg.filled/seg.total*100)}%`}}/>
                  </div>
                  <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.1em',
                    textTransform:'uppercase',fontFamily:'system-ui,sans-serif',
                    color:done?'#6aa36a':'#c4bfba'}}>
                    {seg.label}
                  </div>
                </div>
              );
            })}
          </div>

          {proportionNote && (
            <div style={{display:'flex',gap:8,padding:'10px 14px',backgroundColor:'#fafaf9',
              borderRadius:10,border:'1px solid #f0eeec',animation:'fadeIn 0.3s ease'}}>
              <Info style={{width:12,height:12,color:'#d6d3d1',flexShrink:0,marginTop:1}}/>
              <div style={{fontSize:10,color:'#a8a29e',lineHeight:1.6,fontStyle:'italic',
                fontFamily:'system-ui,sans-serif'}}>{proportionNote}</div>
            </div>
          )}
        </div>

        {/* Preview preset banner */}
        <div style={{...S.card, backgroundColor: usingPreset ? '#fdf8f3' : '#f9f7f5', border: usingPreset ? '1px solid #f0e4d0' : '1px solid #f0eeec', padding:'18px 20px'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: usingPreset ? 12 : 0}}>
            <div>
              <div style={{fontSize:10, fontWeight:700, letterSpacing:'0.15em', color: usingPreset ? '#a07060' : '#78716c', textTransform:'uppercase', fontFamily:'system-ui,sans-serif', marginBottom:4}}>
                Preview Measurements
              </div>
              <div style={{fontSize:11, color:'#78716c', fontFamily:'system-ui,sans-serif', lineHeight:1.5}}>
                {usingPreset
                  ? <span>Using <strong>{PREVIEW_PRESETS[usingPreset].label}</strong> sample measurements — explore freely. <span style={{color:'#a07060'}}>Not valid for Tech Pack purchase.</span></span>
                  : 'Try sample measurements to explore the Salon before measuring yourself.'}
              </div>
            </div>
            {usingPreset && (
              <button onClick={clearPreset} style={{padding:'6px 14px', borderRadius:999, border:'1px solid #e0d0c8', backgroundColor:'#fff', fontSize:9, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#a07060', cursor:'pointer', fontFamily:'system-ui,sans-serif', flexShrink:0, marginLeft:12}}>
                Clear
              </button>
            )}
          </div>
          {!usingPreset && (
            <div style={{display:'flex', gap:8, marginTop:12}}>
              {Object.entries(PREVIEW_PRESETS).map(function([key, preset]) {
                return (
                  <button key={key} onClick={function() { applyPreset(key); }} style={{flex:1, padding:'8px 10px', borderRadius:10, border:'1px solid #e0d9d3', backgroundColor:'#fff', cursor:'pointer', fontFamily:'system-ui,sans-serif', textAlign:'center', transition:'all 0.15s ease'}}>
                    <div style={{fontSize:10, fontWeight:700, color:C, letterSpacing:'0.05em'}}>{preset.label}</div>
                    <div style={{fontSize:9, color:'#a8a29e', marginTop:2}}>{preset.desc}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 5. Measurement groups */}
        {GROUPS.map(grp=>{
          const isOpen=open[grp.id];
          const grpComplete=grp.id==='essential'&&essComplete;
          const grpFlagged=grp.fields.some(f=>flaggedKeys.has(f.key));
          return (
            <div key={grp.id} style={S.card}>
              <button onClick={()=>toggle(grp.id)} style={{
                display:'flex',alignItems:'center',justifyContent:'space-between',
                width:'100%',background:'none',border:'none',cursor:'pointer',padding:0,
                paddingBottom:isOpen?18:0,marginBottom:isOpen?18:0,
                borderBottom:isOpen?'1px dashed #e7e5e4':'none',
                fontFamily:'system-ui,sans-serif',
              }}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:'0.15em',
                    color:'#78716c',textTransform:'uppercase'}}>{grp.label}</span>
                  {grp.id==='essential'&&(
                    <span style={{...S.tag,
                      backgroundColor:grpComplete?'#edf5ed':'#f5f0eb',
                      color:grpComplete?'#4a8a4a':'#8a6a3a'}}>
                      {grpComplete
                        ? <CheckCircle2 style={{width:8,height:8}}/>
                        : <AlertCircle  style={{width:8,height:8}}/>}
                      {grpComplete?'Complete':'Required'}
                    </span>
                  )}
                  {grpFlagged&&(
                    <span style={{...S.tag,backgroundColor:'#fdf7ee',color:'#a07830'}}>
                      <AlertTriangle style={{width:8,height:8}}/> Check
                    </span>
                  )}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:10,color:'#b8b0a8',fontStyle:'italic'}}>
                    {grp.description}
                  </span>
                  {isOpen
                    ? <ChevronUp   style={{width:13,height:13,color:'#a8a29e'}}/>
                    : <ChevronDown style={{width:13,height:13,color:'#a8a29e'}}/>}
                </div>
              </button>

              {isOpen&&(
                <div style={{
                  display:grp.id==='notes'?'flex':'grid',
                  flexDirection:grp.id==='notes'?'column':undefined,
                  gridTemplateColumns:grp.id!=='notes'?'repeat(2,1fr)':undefined,
                  gap:18,animation:'fadeIn 0.2s ease',
                }}>
                  {grp.fields.map(field=>(
                    <div key={field.key}>
                      <label style={{...S.lbl,
                        color:flaggedKeys.has(field.key)?'#b87840':'#a8a29e'}}>
                        {field.label}{flaggedKeys.has(field.key)&&' ⚠'}
                      </label>
                      <NumInput field={field} value={m[field.key]}
                        onChange={change} flagged={flaggedKeys.has(field.key)}/>
                      {field.hint&&field.type!=='text'&&
                        <div style={S.hint}>{field.hint}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Skin Tone — standalone always-visible card */}
        <div style={{...S.card, padding:'20px 24px'}}>
          <div style={{fontSize:11, fontWeight:700, color:C, fontFamily:'system-ui,sans-serif', marginBottom:4}}>
            Render Skin Tone <span style={{fontWeight:400, color:'#a8a29e', fontSize:10, textTransform:'none', letterSpacing:0, fontStyle:'italic'}}>(Optional — helps personalise your Salon render)</span>
          </div>
          <div style={{fontSize:10, color:'#78716c', fontFamily:'system-ui,sans-serif', lineHeight:1.6, marginBottom:14}}>
            Select the closest match. AI rendering is approximate.
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8}}>
            {SKIN_TONE_OPTIONS.map(function(opt) {
              const selected = m.skinTone === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={function() { change('skinTone', selected ? '' : opt.value); }}
                  style={{
                    display:'flex', alignItems:'center', gap:8,
                    padding:'8px 10px', borderRadius:10,
                    border: selected ? '1.5px solid #C9A0A0' : '1.5px solid #e7e5e4',
                    backgroundColor: selected ? '#fdf5f5' : '#fff',
                    cursor:'pointer', fontFamily:'system-ui,sans-serif',
                  }}
                >
                  <div style={{
                    width:18, height:18, borderRadius:'50%',
                    backgroundColor: opt.swatch,
                    border:'1px solid rgba(0,0,0,0.1)',
                    flexShrink:0,
                  }}/>
                  <span style={{fontSize:10, fontWeight: selected ? 700 : 400, color: selected ? '#2D2926' : '#78716c'}}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 6. Measurement consent */}
        {essComplete && !hasErrors && (
          <div style={{...S.card, padding:'20px 24px', border: consent ? '1px solid #c8e0c8' : '1px solid #f0eeec', backgroundColor: consent ? '#f8fcf8' : '#fff', transition:'all 0.3s ease'}}>
            <div style={{display:'flex',alignItems:'flex-start',gap:14,cursor:'pointer'}} onClick={function() { setConsent(function(p) { return !p; }); }}>
              <div style={{
                width:20,height:20,borderRadius:6,flexShrink:0,marginTop:1,
                display:'flex',alignItems:'center',justifyContent:'center',
                border: consent ? 'none' : '2px solid #c4bfba',
                backgroundColor: consent ? '#6aa36a' : '#fff',
                transition:'all 0.2s ease',
              }}>
                {consent && <CheckCircle2 style={{width:14,height:14,color:'#fff'}}/>}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C,fontFamily:'system-ui,sans-serif',marginBottom:8,lineHeight:1.4}}>
                  I consent to Tabitha Studio storing my measurements
                </div>
                <div style={{fontSize:10,color:'#78716c',fontFamily:'system-ui,sans-serif',lineHeight:1.7}}>
                  Your measurements are personal. We treat them with particular care:
                </div>
                <ul style={{margin:'6px 0 0',paddingLeft:16,fontSize:10,color:'#78716c',fontFamily:'system-ui,sans-serif',lineHeight:1.8,listStyleType:'"— "'}}>
                  <li>Stored encrypted at rest and accessible only to you</li>
                  <li>Never sold, licensed, or shared with third parties</li>
                  <li>Never included in any prompt that identifies you personally</li>
                  <li>You can delete them at any time by clearing your saved data</li>
                </ul>
                <div style={{fontSize:10,color:'#a8a29e',fontFamily:'system-ui,sans-serif',lineHeight:1.7,marginTop:6,fontStyle:'italic'}}>
                  If you use Choozr or another measurement app, that app's own privacy policy governs how it handles your data. Once measurements are transferred to Tabitha Studio, this policy applies.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 7. Export measurements */}
        {essComplete && (
          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button
              onClick={function() {
                const lines = [
                  'Tabitha — Measurement Profile',
                  'Exported: ' + new Date().toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'}),
                  '',
                  'ESSENTIAL',
                  'Height:           ' + (m.height      || '—') + '"',
                  'Bust:             ' + (m.bust        || '—') + '"',
                  'High Bust:        ' + (m.highBust    || '—') + '"',
                  'Natural Waist:    ' + (m.waist       || '—') + '"',
                  'Hips:             ' + (m.hips        || '—') + '"',
                  'Hollow to Hem:    ' + (m.hollowToHem || '—') + '"',
                  '',
                  'UPPER BODY',
                  'Shoulder Width:   ' + (m.shoulder     || '—') + '"',
                  'Sleeve Length:    ' + (m.sleeveLength || '—') + '"',
                  'Bicep:            ' + (m.bicep        || '—') + '"',
                  '',
                  'TORSO',
                  'Front Waist:      ' + (m.frontWaist  || '—') + '"',
                  'Back Waist:       ' + (m.backWaist   || '—') + '"',
                  'Waist to Hip:     ' + (m.waistToHip  || '—') + '"',
                  '',
                  'FIT NOTES',
                  'Posture:          ' + (m.posture        || '—'),
                  'Fit Preference:   ' + (m.fitPreference  || '—'),
                  '',
                  '© Tabitha 2026 — Keep this file for your records.',
                ];
                const blob = new Blob([lines.join('\n')], {type:'text/plain'});
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href     = url;
                a.download = 'Tabitha-Measurements.txt';
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                display:'flex',alignItems:'center',gap:7,
                padding:'10px 18px',borderRadius:12,
                border:'1px solid #e7e5e4',backgroundColor:'#fafaf9',
                color:'#78716c',fontSize:10,fontWeight:700,
                letterSpacing:'0.15em',textTransform:'uppercase',
                cursor:'pointer',fontFamily:'system-ui,sans-serif',
              }}
            >
              <ExternalLink style={{width:12,height:12}}/> Export Measurements
            </button>
          </div>
        )}

        {/* 8. Proceed / Reset */}
        <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:12}}>
          <button onClick={handleProceed} disabled={!canSave} style={{
            display:'flex',alignItems:'center',justifyContent:'center',gap:10,
            padding:'16px 0',
            backgroundColor:canSave?'#C9A0A0':'#e7e5e4',
            color:canSave?'#2D2926':'#b8b0a8',
            fontWeight:700,fontSize:11,letterSpacing:'0.35em',textTransform:'uppercase',
            borderRadius:14,border:'none',cursor:canSave?'pointer':'not-allowed',
            boxShadow:canSave?'0 4px 20px rgba(45,41,38,0.22)':'none',
            fontFamily:'system-ui,sans-serif',transition:'all 0.2s ease',
          }}>
            <Save style={{width:14,height:14}}/>
            {!essComplete?'Complete Essential Measurements to Proceed'
              :hasErrors?'Resolve Errors to Proceed'
              :isPreview?'Preview The Salon →'
              :!consent?'Consent Required to Proceed'
              :'Proceed to The Salon'}
          </button>
          <button onClick={()=>{setM(INITIAL);setSaved(false);}} style={{
            display:'flex',alignItems:'center',justifyContent:'center',gap:7,
            padding:'16px 20px',backgroundColor:'#fafaf9',color:'#78716c',
            fontSize:10,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase',
            borderRadius:14,border:'1px solid #e7e5e4',cursor:'pointer',
            fontFamily:'system-ui,sans-serif',
          }}>
            <RotateCcw style={{width:13,height:13}}/> Reset
          </button>
        </div>

        {/* 9. Footer */}
        <div style={{textAlign:'center',fontSize:9,color:'#b8b0a8',letterSpacing:'0.15em',
          paddingTop:4,fontFamily:'system-ui,sans-serif'}}>
          © 2026 Tabitha Studio. All rights reserved.
        </div>

      </div>
    </div>
  );
}
